/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * EGM96 geoid heights — turns stored GPS altitude into altitude above mean sea
 * level, exactly as the AthloHive app does (Egm96Geoid + LegacyAltitudeCorrector).
 *
 * GPS reports height above the WGS84 ellipsoid, not sea level; the two differ
 * by the geoid undulation N (about −60 to −107 m across India, −86 m in
 * Bengaluru), so MSL = ellipsoidal − N. Activities recorded by newer app
 * builds already store sea-level altitude and say so with
 * activity_meta.altitudeRef = "msl"; older ones store the raw ellipsoidal
 * value and are corrected at display time. Without this the web draws every
 * such activity ~86 m lower than the app.
 *
 * public/geoid/egm96_30min.bin is the app's own asset, byte for byte: ROWS ×
 * COLS big-endian int16 heights in decimetres every 0.5°, row 0 at 90°N,
 * column 0 at 180°W (downsampled from the public-domain NGA EGM96 grid).
 */

export const GEOID_URL = `${process.env.PUBLIC_URL || ''}/geoid/egm96_30min.bin`;

const STEP_DEG = 0.5;
const ROWS = 181 * 2 - 1; // 90°N..90°S inclusive
const COLS = 360 * 2; // 180°W..179.5°E; 180°E wraps to column 0

/** Raw grid bytes → { undulationM(lat, lng) }, or null when the bytes are not the grid. */
export function parseGeoid(buffer) {
  if (!buffer || buffer.byteLength !== ROWS * COLS * 2) return null;
  const view = new DataView(buffer);
  const node = (row, col) => view.getInt16((row * COLS + col) * 2, false);
  return {
    /** Geoid undulation N in metres (bilinear between grid nodes). */
    undulationM(lat, lng) {
      const clampedLat = Math.max(-90, Math.min(90, lat));
      const wrappedLng = (((lng + 180) % 360) + 360) % 360;
      const fy = (90 - clampedLat) / STEP_DEG;
      const fx = wrappedLng / STEP_DEG;
      const y0 = Math.min(Math.floor(fy), ROWS - 2);
      const x0 = Math.floor(fx) % COLS;
      const x1 = (x0 + 1) % COLS;
      const ty = fy - y0;
      const tx = fx - Math.floor(fx);
      const top = node(y0, x0) * (1 - tx) + node(y0, x1) * tx;
      const bottom = node(y0 + 1, x0) * (1 - tx) + node(y0 + 1, x1) * tx;
      return (top * (1 - ty) + bottom * ty) / 10;
    },
  };
}

/** Fetches the grid; resolves null (never rejects) when it cannot be had. */
export async function loadGeoid(fetchImpl = fetch) {
  try {
    const res = await fetchImpl(GEOID_URL);
    if (!res.ok) return null;
    return parseGeoid(await res.arrayBuffer());
  } catch (e) {
    return null;
  }
}

/** True when the activity's stored altitudes are already sea level. */
export function isAltitudeMsl(meta) {
  return !!meta && meta.altitudeRef === 'msl';
}

/**
 * Route for display, as the app's LegacyAltitudeCorrector: an altitude of
 * exactly 0 is the "no reading" placeholder and becomes null (never shifted —
 * that would invent ~86 m in Bengaluru), and the rest are converted to sea
 * level unless the activity is already sea-level or the grid is unavailable.
 * Returns new points; the route itself (and the route cache) stays raw.
 */
export function routeToSeaLevel(route, meta, geoid) {
  if (!Array.isArray(route) || !route.length) return route;
  const toMsl = !!geoid && !isAltitudeMsl(meta);
  return route.map((p) => {
    if (p.alt === null || p.alt === undefined) return p;
    if (p.alt === 0) return { ...p, alt: null };
    return toMsl ? { ...p, alt: p.alt - geoid.undulationM(p.lat, p.lng) } : p;
  });
}
